import { Injectable } from '@angular/core';
import { PagedResultGeneric } from 'src/app/shared';
import { PostTransactionsSearchDto } from './dto/post-transactions-search.dto';
import { PostTransactionSummaryOptionsDto } from './dto/unposted-transaction-summary-options.dto';
import { FinancialApiService } from './financial-api.service';
import { AccountingPackageDto } from './revenue-items/dto/accounting-package.dto';
import { AccountingPackageJournalEntry } from './transaction/dto/accounting-package-journal-entry.dto';
import { PostedTransactionDto } from './transaction/dto/posted-transaction.dto';
import { PostExistingTransactionDto } from './transaction/dto/post-existing-transaction.dto';
import { WriteOffInvoicesDto } from './dto/write-off-invoices.dto';
import { InvoiceUpsertResultDto } from 'src/app/invoices/dto/invoice-upsert-result.dto';
import { PostTransactionSummaryDto } from './dto/post-transaction-summary.dto';
import { TransactionSummarySearchDto } from './transaction/transaction-summary-search.dto';
import { MultiProfileInvoiceDto } from 'src/app/profiles/search/dto/multi-profile-invoice.dto';
import { InvViewDto } from './dto/inv-view.dto';
import { InvViewSearchDto } from './dto/inv-view-search.dto';
import { AccountDto } from './accounting-package/dto/account.dto';
import { BillingAmountUpdateDto } from '../billing-amount-update/dto/billing-amount-update.dto';
import { DebitCreditAccountValidationResultDto } from './dto/debit-credit-account.validation-result.dto';
import { AdjustmentType } from './adjustment-types/adjustment-type.model';

@Injectable({
  providedIn: 'root',
})
export class FinancialServiceService {
  public constructor(private _apiService: FinancialApiService) {}

  getAccountingPackages(): Promise<AccountingPackageDto[]> {
    return this._apiService.get('financial/v2/accountingpackages');
  }

  getTransactions(searchParams): Promise<PagedResultGeneric<PostedTransactionDto>> {
    return this._apiService.post('financial/v2/transaction/search', searchParams);
  }

  getJournalEntries(searchParams): Promise<PagedResultGeneric<AccountingPackageJournalEntry>> {
    return this._apiService.post('financial/v2/transaction/journalentries/search', searchParams);
  }

  exportTransactions(searchParams): Promise<string> {
    return this._apiService.post('financial/v2/transaction/export', searchParams);
  }

  exportJournalEntries(searchParams): Promise<string> {
    return this._apiService.post('financial/v2/transaction/journalentries/export', searchParams);
  }
  searchUnpostedTransactions(searchParams) {
    return this._apiService.post('financial/v2/unpostedtransactions/search', searchParams);
  }
  exportUnpostedTransactions(searchParams) {
    return this._apiService.post('financial/v2/unpostedtransactions/export', searchParams);
  }
  validateUnpostedTransactions(searchParams): Promise<DebitCreditAccountValidationResultDto> {
    return this._apiService.post('financial/v2/unpostedtransactions/validatesearch', searchParams);
  }
  postExistingTransactions(postExistingTransactionDto: PostExistingTransactionDto) {
    return this._apiService.put('financial/v2/unpostedtransactions/postexistingtransactions', postExistingTransactionDto);
  }

  summarizeTransactions(searchParams: PostTransactionSummaryOptionsDto): Promise<Array<PostTransactionSummaryDto>> {
    return this._apiService.post('financial/v2/unpostedtransactions/summarize', searchParams);
  }

  exportTransactionSummaries(searchParams: PostTransactionsSearchDto): Promise<string> {
    return this._apiService.post('financial/v2/unpostedtransactions/summarize/export', searchParams);
  }

  getAllTranCodes(): Promise<Array<string>> {
    return this._apiService.get('financial/v2/unpostedtransactions/trancodes');
  }

  public writeOffInvoices(dto: WriteOffInvoicesDto): Promise<InvoiceUpsertResultDto> {
    return this._apiService.post('financial/v2/invoice/writeOff', dto);
  }

  public multiProfileInvoiceUpsert(dto: MultiProfileInvoiceDto) {
    return this._apiService.post('financial/v2/invoice/multiProfileInvoiceUpsert', dto);
  }

  validateQuickBooksOnlineConnectiion(accountingPackageId: number): Promise<boolean> {
    return this._apiService.get(`financial/v2/QuickBooksOnline/validateQuickBooksOnlineConnection/${accountingPackageId}`);
  }

  getArbCreationActivity(searchParams: TransactionSummarySearchDto) {
    return this._apiService.post('financial/v2/revenue/ArbActivitySearch', searchParams);
  }

  public searchInvoiceLineItems(search: InvViewSearchDto): Promise<PagedResultGeneric<InvViewDto>> {
    return this._apiService.post('financial/v2/invoiceLineItem/search', search);
  }

  public getAccountsByAccountingPackage(id: number): Promise<Array<AccountDto>> {
    return this._apiService.get(`financial/v2/accountingpackages/${id}/accounts`);
  }

  public exportProposedBillingChanges(settings: BillingAmountUpdateDto): Promise<string> {
    return this._apiService.post(`financial/v2/billing/AmountUpdate/Preview/Export`, settings);
  }

  public getAdjustmentTypesByAccountingPackages(accountingPackageIds: Array<number>): Promise<Array<AdjustmentType>> {
    return this._apiService.post(`financial/v1/AdjustmentType/by-accounting-packages`, accountingPackageIds);
  }
}
